import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Upload,
  Download,
  ChevronDown,
  ChevronRight,
  NotebookTabs,
} from "lucide-react";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import { PageHeader, PageShell } from "@/components/PageShell";
import { InlineHint, PageEmptyState, PageLoadingState, SectionLoadingState } from "@/components/PageStates";
import { getTodayInBusinessTz } from "@/lib/date";
import { getKategoriIcon, getKategoriTheme } from "@/lib/categoryPresentation";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";
import { formatStockValue } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_TIME_START = "2000-01-01";

const getTodayDateInput = () => getTodayInBusinessTz();
const getFirstDayOfMonthInput = () => {
  const today = getTodayDateInput();
  const [year, month] = today.split("-");
  return `${year}-${month}-01`;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        const msg = item?.msg;
        const loc = Array.isArray(item?.loc)
          ? item.loc.filter((part) => part !== "body").join(".")
          : "";
        if (msg && loc) return `${msg} (${loc})`;
        if (msg) return msg;
        return null;
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join("; ");
    }
  }

  if (typeof detail === "string" && detail.trim() !== "") {
    return detail;
  }

  return fallbackMessage;
};

const parseKonversiInput = (inputText, satuanDasar) => {
  const konversi = {};
  let resolvedSatuanDasar = (satuanDasar || "").trim().toLowerCase();

  if (typeof inputText === "string" && inputText.trim() !== "") {
    const entries = inputText.split(",");
    for (const rawEntry of entries) {
      const entry = rawEntry.trim();
      if (!entry) continue;

      const naturalMatch = entry.match(/^\s*1?\s*([a-zA-Z]+)\s*=\s*([0-9]+(?:\.[0-9]+)?)\s*([a-zA-Z]+)\s*$/i);
      if (naturalMatch) {
        const fromUnit = naturalMatch[1].trim().toLowerCase();
        const factor = parseFloat(naturalMatch[2]);
        const toUnit = naturalMatch[3].trim().toLowerCase();
        if (fromUnit && toUnit && Number.isFinite(factor) && factor > 0) {
          resolvedSatuanDasar = toUnit;
          konversi[fromUnit] = factor;
        }
        continue;
      }

      const [rawSatuan, rawFaktor] = entry.split("=");
      const satuan = (rawSatuan || "").trim().toLowerCase();
      const faktor = parseFloat((rawFaktor || "").trim());
      if (satuan && Number.isFinite(faktor) && faktor > 0) {
        konversi[satuan] = faktor;
      }
    }
  }

  if (resolvedSatuanDasar) {
    konversi[resolvedSatuanDasar] = 1;
  }

  return {
    konversi,
    satuanDasar: resolvedSatuanDasar || (satuanDasar || "").trim().toLowerCase(),
  };
};

const formatKonversiInput = (konversiSatuan, satuanDasar) => {
  if (!konversiSatuan || typeof konversiSatuan !== "object") {
    return "";
  }
  const base = (satuanDasar || "").trim().toLowerCase();
  return Object.entries(konversiSatuan)
    .filter(([satuan, faktor]) => satuan !== base && Number(faktor) > 0)
    .map(([satuan, faktor]) => `${satuan}=${faktor}`)
    .join(", ");
};

const normalizeKonversiForCompare = (konversiSatuan = {}) => {
  return Object.entries(konversiSatuan || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([satuan, faktor]) => [satuan, Number(faktor)]);
};

const MasterBarangPage = () => {
  const [barang, setBarang] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [kartuStokDialogOpen, setKartuStokDialogOpen] = useState(false);
  const [selectedBarangDetail, setSelectedBarangDetail] = useState(null);
  const [kartuStok, setKartuStok] = useState(null);
  const [loadingKartuStok, setLoadingKartuStok] = useState(false);
  const [kartuStokFilter, setKartuStokFilter] = useState({
    tanggalMulai: ALL_TIME_START,
    tanggalAkhir: getTodayDateInput(),
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState("all");
  const [stokAwalBulan, setStokAwalBulan] = useState(null);
  const [loadingStokAwalBulan, setLoadingStokAwalBulan] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const [formData, setFormData] = useState({
    nama_barang: "",
    kategori_id: "",
    satuan: "",
    original_satuan: "",
    konversi_satuan_text: "",
    min_stok: 0,
    stok_current: 0,
    stok_adjust_mode: "Tambah",
    stok_adjust_qty: "",
    stok_adjust_note: "",
    aktif: true,
  });
  const [currentId, setCurrentId] = useState(null);
  const [editingSnapshot, setEditingSnapshot] = useState(null);
  const namaBarangInputRef = useRef(null);
  const kategoriSelectRef = useRef(null);
  const satuanInputRef = useRef(null);
  const konversiInputRef = useRef(null);
  const minStokInputRef = useRef(null);
  const stokCurrentInputRef = useRef(null);
  const stokAdjustModeRef = useRef(null);
  const stokAdjustQtyInputRef = useRef(null);
  const stokAdjustNoteRef = useRef(null);
  const barangCancelButtonRef = useRef(null);
  const barangSubmitButtonRef = useRef(null);

  const focusBarangFormEnd = () => {
    if (editMode) {
      stokAdjustNoteRef.current?.focus();
      return;
    }

    stokCurrentInputRef.current?.focus();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [barangRes, kategoriRes] = await Promise.all([
        axios.get(`${API}/barang`),
        axios.get(`${API}/kategori`),
      ]);
      setBarang(barangRes.data);
      setKategori(kategoriRes.data);
      // Initialize all categories as expanded
      const expanded = {};
      kategoriRes.data.forEach(k => {
        expanded[k.kategori_id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStokAwalBulanIni = async (barangId) => {
    const today = getTodayDateInput();
    const [year, month] = today.split("-");
    const tanggalMulai = `${year}-${month}-01`;

    setLoadingStokAwalBulan(true);
    try {
      const response = await axios.get(
        `${API}/laporan/kartu-stok?barang_id=${barangId}&tanggal_mulai=${tanggalMulai}&tanggal_akhir=${today}`
      );
      setStokAwalBulan(Number(response.data?.saldo_awal ?? 0));
    } catch {
      setStokAwalBulan(null);
    } finally {
      setLoadingStokAwalBulan(false);
    }
  };

  const toggleCategory = (kategoriId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [kategoriId]: !prev[kategoriId]
    }));
  };

  const getBarangByKategori = (kategoriId) => {
    return filteredBarang.filter(item => item.kategori_id === kategoriId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmDialog({
      open: true,
      title: editMode ? "Konfirmasi Perbarui Barang" : "Konfirmasi Simpan Barang",
      description: editMode
        ? "Apakah Anda yakin ingin memperbarui data barang ini?"
        : "Apakah Anda yakin ingin menyimpan data barang ini?",
      confirmText: editMode ? "Ya, Perbarui" : "Ya, Simpan",
      destructive: false,
      onConfirm: async () => {
        try {
          if (editMode) {
            const parsedKonversi = parseKonversiInput(
              formData.konversi_satuan_text,
              formData.satuan
            );
            const targetSatuan = parsedKonversi.satuanDasar || formData.satuan;
            const factorFromOriginal = parsedKonversi.konversi[formData.original_satuan?.toLowerCase() || ""] || 1;
            const unitChanged =
              formData.original_satuan &&
              formData.original_satuan.toLowerCase() !== String(targetSatuan).toLowerCase();
            const adjustQty = Number(formData.stok_adjust_qty || 0);

            const payload = {
              nama_barang: formData.nama_barang,
              kategori_id: formData.kategori_id,
              satuan: targetSatuan,
              konversi_satuan: parsedKonversi.konversi,
              aktif: formData.aktif,
              min_stok:
                unitChanged
                  ? Number(formData.min_stok) * Number(factorFromOriginal)
                  : Number(formData.min_stok),
            };
            if (unitChanged) {
              payload.stok_current = Number(formData.stok_current) * Number(factorFromOriginal);
            }

            const snapshot = editingSnapshot || {};
            const konversiChanged =
              JSON.stringify(normalizeKonversiForCompare(payload.konversi_satuan)) !==
              JSON.stringify(normalizeKonversiForCompare(snapshot.konversi_satuan || {}));

            const hasMasterChanges =
              payload.nama_barang !== snapshot.nama_barang ||
              payload.kategori_id !== snapshot.kategori_id ||
              payload.satuan !== snapshot.satuan ||
              Number(payload.min_stok) !== Number(snapshot.min_stok || 0) ||
              Boolean(payload.aktif) !== Boolean(snapshot.aktif) ||
              konversiChanged ||
              (unitChanged && Number(payload.stok_current || 0) !== Number(snapshot.stok_current || 0));

            if (hasMasterChanges) {
              await axios.put(`${API}/barang/${currentId}`, payload);
            }

            if (adjustQty > 0) {
              await axios.post(`${API}/barang/${currentId}/adjust-stock`, {
                mode: formData.stok_adjust_mode,
                qty: adjustQty,
                keterangan: formData.stok_adjust_note,
              });
            }

            if (!hasMasterChanges && adjustQty <= 0) {
              toast.error("Tidak ada perubahan yang disimpan");
              return;
            }

            toast.success(
              hasMasterChanges && adjustQty > 0
                ? "Barang berhasil diupdate dan stok disesuaikan"
                : adjustQty > 0
                  ? "Penyesuaian stok berhasil disimpan"
                : "Barang berhasil diupdate"
            );
          } else {
            const parsedKonversi = parseKonversiInput(
              formData.konversi_satuan_text,
              formData.satuan
            );
            const payload = {
              ...formData,
              satuan: parsedKonversi.satuanDasar || formData.satuan,
              konversi_satuan: parsedKonversi.konversi,
            };
            delete payload.konversi_satuan_text;
            delete payload.original_satuan;
            delete payload.stok_adjust_mode;
            delete payload.stok_adjust_qty;
            delete payload.stok_adjust_note;
            await axios.post(`${API}/barang`, payload);
            toast.success("Barang berhasil ditambahkan");
          }
          setDialogOpen(false);
          resetForm();
          fetchData();
        } catch (error) {
          toast.error(getApiErrorMessage(error, "Gagal menyimpan data"));
        }
      },
    });
  };

  const handleEdit = (item) => {
    setFormData({
      nama_barang: item.nama_barang,
      kategori_id: item.kategori_id,
      satuan: item.satuan,
      original_satuan: item.satuan,
      konversi_satuan_text: formatKonversiInput(item.konversi_satuan, item.satuan),
      min_stok: item.min_stok,
      stok_current: item.stok_current,
      stok_adjust_mode: "Tambah",
      stok_adjust_qty: "",
      stok_adjust_note: "",
      aktif: item.aktif,
    });
    setCurrentId(item.barang_id);
    setEditingSnapshot(item);
    setEditMode(true);
    setDialogOpen(true);
    fetchStokAwalBulanIni(item.barang_id);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Barang",
      description: "Apakah Anda yakin ingin menghapus barang ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/barang/${id}`);
          toast.success("Barang berhasil dihapus");
          fetchData();
        } catch (error) {
          toast.error("Gagal menghapus barang");
        }
      },
    });
  };

  const resetForm = () => {
    setFormData({
      nama_barang: "",
      kategori_id: "",
      satuan: "",
      original_satuan: "",
      konversi_satuan_text: "",
      min_stok: 0,
      stok_current: 0,
      stok_adjust_mode: "Tambah",
      stok_adjust_qty: "",
      stok_adjust_note: "",
      aktif: true,
    });
    setStokAwalBulan(null);
    setEditMode(false);
    setCurrentId(null);
    setEditingSnapshot(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/template/import-barang`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Template_Import_Barang.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Template berhasil didownload");
    } catch (error) {
      toast.error("Gagal download template");
      console.error(error);
    }
  };

  const handleImportExcel = async () => {
    if (!uploadFile) {
      toast.error("Pilih file Excel terlebih dahulu");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const response = await axios.post(`${API}/barang/import-excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.log("Import errors:", response.data.errors);
        toast.warning(`${response.data.errors.length} baris error. Check console.`);
      }
      setImportDialogOpen(false);
      setUploadFile(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Gagal import data"));
    }
  };

  const fetchKartuStok = async (barangId, tanggalMulai, tanggalAkhir) => {
    setLoadingKartuStok(true);
    try {
      const response = await axios.get(
        `${API}/laporan/kartu-stok?barang_id=${barangId}&tanggal_mulai=${tanggalMulai}&tanggal_akhir=${tanggalAkhir}`
      );
      setKartuStok(response.data);
    } catch (error) {
      toast.error("Gagal memuat kartu stok");
    } finally {
      setLoadingKartuStok(false);
    }
  };

  const handleOpenKartuStok = (item) => {
    const defaultFilter = {
      tanggalMulai: ALL_TIME_START,
      tanggalAkhir: getTodayDateInput(),
    };
    setSelectedBarangDetail(item);
    setKartuStokFilter(defaultFilter);
    setActiveQuickFilter("all");
    setKartuStokDialogOpen(true);
    fetchKartuStok(item.barang_id, defaultFilter.tanggalMulai, defaultFilter.tanggalAkhir);
  };

  const handleApplyKartuStokFilter = async (e) => {
    e.preventDefault();
    if (!selectedBarangDetail) {
      return;
    }
    if (kartuStokFilter.tanggalMulai > kartuStokFilter.tanggalAkhir) {
      toast.error("Tanggal mulai tidak boleh lebih besar dari tanggal akhir");
      return;
    }
    await fetchKartuStok(
      selectedBarangDetail.barang_id,
      kartuStokFilter.tanggalMulai,
      kartuStokFilter.tanggalAkhir
    );
    setActiveQuickFilter("custom");
  };

  const handleQuickKartuStokFilter = async (mode) => {
    if (!selectedBarangDetail) {
      return;
    }

    const today = getTodayDateInput();
    const quickFilter =
      mode === "today"
        ? { tanggalMulai: today, tanggalAkhir: today }
        : mode === "month"
          ? { tanggalMulai: getFirstDayOfMonthInput(), tanggalAkhir: today }
          : { tanggalMulai: ALL_TIME_START, tanggalAkhir: today };

    setKartuStokFilter(quickFilter);
    setActiveQuickFilter(mode);
    await fetchKartuStok(
      selectedBarangDetail.barang_id,
      quickFilter.tanggalMulai,
      quickFilter.tanggalAkhir
    );
  };

  const filteredBarang = barang.filter((item) =>
    item.nama_barang.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const adjustmentQty = Number(formData.stok_adjust_qty || 0);
  const adjustmentDelta =
    adjustmentQty > 0
      ? formData.stok_adjust_mode === "Tambah"
        ? adjustmentQty
        : -adjustmentQty
      : 0;
  const stokSetelahPenyesuaian = Number(formData.stok_current || 0) + adjustmentDelta;
  const kategoriAktifDenganBarang = kategori.filter(
    (kat) => getBarangByKategori(kat.kategori_id).length > 0
  ).length;

  if (loading) {
    return <PageLoadingState label="Memuat master barang" description="Mengambil daftar barang, kategori, dan struktur stok terbaru." />;
  }

  return (
    <PageShell data-testid="master-barang-page">
      <PageHeader
        eyebrow="Master Data"
        title="Master Barang"
        description="Kelola katalog barang, satuan, dan stok minimum dengan struktur yang lebih konsisten."
        actions={(
          <div className="flex w-full flex-col gap-2 md:w-auto md:items-end">
            <div className="grid w-full gap-2 sm:grid-cols-2 md:flex md:flex-wrap md:justify-end">
              <Button
                data-testid="download-template-button"
                variant="outline"
                onClick={handleDownloadTemplate}
                className="w-full md:w-auto"
              >
                <Download size={16} className="mr-2" />
                Template
              </Button>
              <Button
                data-testid="import-excel-button"
                variant="outline"
                onClick={() => setImportDialogOpen(true)}
                className="w-full md:w-auto"
              >
                <Upload size={16} className="mr-2" />
                Import Excel
              </Button>
            </div>
            <Button
              data-testid="add-barang-button"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="w-full md:w-auto"
            >
              <Plus size={16} className="mr-2" />
              Tambah Barang
            </Button>
          </div>
        )}
      />

      <div className="rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex-1 space-y-2">
            <Label>Cari Barang</Label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={20}
              />
              <Input
                data-testid="search-barang-input"
                placeholder="Cari nama barang, satuan, atau kategori"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:min-w-[260px]">
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Kategori tampil</p>
              <p className="mt-1 font-manrope text-lg font-bold text-slate-900">{kategoriAktifDenganBarang}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Barang tampil</p>
              <p className="mt-1 font-manrope text-lg font-bold text-slate-900">{filteredBarang.length}</p>
            </div>
          </div>
        </div>
        <InlineHint className="mt-4 border-slate-200 bg-slate-50/80">
          Pada layar kecil, data barang tampil sebagai kartu ringkas. Pada layar besar, tampilan tabel tetap dipakai agar proses scan data lebih cepat.
        </InlineHint>
      </div>

      {/* Grouped by Category View */}
      <div className="space-y-4">
        {filteredBarang.length === 0 ? (
          <PageEmptyState
            title="Belum ada barang yang cocok"
            description="Coba ubah kata kunci pencarian atau tambahkan barang baru agar kategori mulai terisi."
          />
        ) : (
          kategori
            .filter(kat => getBarangByKategori(kat.kategori_id).length > 0)
            .map((kat) => {
              const items = getBarangByKategori(kat.kategori_id);
              const isExpanded = expandedCategories[kat.kategori_id];
              const KategoriIcon = getKategoriIcon(kat.nama_kategori);
              const kategoriTheme = getKategoriTheme(kat.nama_kategori);
              
              return (
                <div
                  key={kat.kategori_id}
                  className={`rounded-lg border bg-card overflow-hidden ${kategoriTheme.cardBorder}`}
                >
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(kat.kategori_id)}
                    className={`w-full px-4 py-3 flex items-center justify-between transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${kategoriTheme.headerBg}`}
                    aria-expanded={isExpanded}
                    aria-controls={`kategori-panel-${kat.kategori_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown size={20} className={kategoriTheme.iconText} />
                      ) : (
                        <ChevronRight size={20} className={kategoriTheme.iconText} />
                      )}
                      <KategoriIcon size={20} className={kategoriTheme.iconText} />
                      <span className={`font-manrope font-semibold ${kategoriTheme.titleText}`}>
                        {kat.nama_kategori}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${kategoriTheme.badgeClass}`}>
                        {items.length} item
                      </span>
                    </div>
                  </button>
                  
                  {/* Category Items */}
                  {isExpanded && (
                    <div id={`kategori-panel-${kat.kategori_id}`}>
                      <div className="space-y-3 p-4 md:hidden">
                        {items.map((item) => (
                          <div
                            key={item.barang_id}
                            data-testid={`barang-row-${item.barang_id}`}
                            className="rounded-xl border border-slate-200/80 bg-white/90 p-4 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-manrope font-semibold text-slate-900">{item.nama_barang}</p>
                                <p className="text-sm text-muted-foreground">Satuan: {item.satuan}</p>
                              </div>
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                  item.aktif
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {item.aktif ? "Aktif" : "Nonaktif"}
                              </span>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Min Stok</p>
                                <p>{item.min_stok}</p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Stok Saat Ini</p>
                                <p className={`font-manrope font-bold ${item.stok_current <= item.min_stok ? "text-destructive" : "text-green-600"}`}>
                                  {formatStockValue(item.stok_current)}
                                </p>
                              </div>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2">
                              <Button
                                data-testid={`detail-barang-${item.barang_id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenKartuStok(item)}
                                className="w-full"
                              >
                                <NotebookTabs size={16} className="mr-1" />
                                Stok
                              </Button>
                              <Button
                                data-testid={`edit-barang-${item.barang_id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(item)}
                                className="w-full"
                              >
                                <Edit size={16} className="mr-1" />
                                Edit
                              </Button>
                              <Button
                                data-testid={`delete-barang-${item.barang_id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(item.barang_id)}
                                className="w-full"
                              >
                                <Trash2 size={16} className="mr-1 text-destructive" />
                                Hapus
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="hidden overflow-x-auto md:block">
                        <table className="min-w-[820px] w-full">
                          <thead>
                            <tr className={`border-b ${kategoriTheme.tableHeadBg}`}>
                              <th className="px-4 py-2 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Nama Barang
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Satuan
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Min Stok
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Stok Saat Ini
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Status
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                                Aksi
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => (
                              <tr
                                key={item.barang_id}
                                data-testid={`barang-row-${item.barang_id}`}
                                className="border-b transition-colors hover:bg-muted/50"
                              >
                                <td className="px-4 py-3 font-medium">
                                  {item.nama_barang}
                                </td>
                                <td className="px-4 py-3">{item.satuan}</td>
                                <td className="px-4 py-3">{item.min_stok}</td>
                                <td className="px-4 py-3">
                                    <span
                                      className={`font-manrope font-bold ${
                                        item.stok_current <= item.min_stok
                                          ? "text-destructive"
                                          : "text-green-600"
                                      }`}
                                    >
                                    {formatStockValue(item.stok_current)}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      item.aktif
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {item.aktif ? "Aktif" : "Nonaktif"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right space-x-2">
                                  <Button
                                    data-testid={`detail-barang-${item.barang_id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenKartuStok(item)}
                                  >
                                    <NotebookTabs size={16} />
                                  </Button>
                                  <Button
                                    data-testid={`edit-barang-${item.barang_id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(item)}
                                  >
                                    <Edit size={16} />
                                  </Button>
                                  <Button
                                    data-testid={`delete-barang-${item.barang_id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(item.barang_id)}
                                  >
                                    <Trash2 size={16} className="text-destructive" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Edit Barang" : "Tambah Barang"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Barang</Label>
              <Input
                ref={namaBarangInputRef}
                data-testid="form-nama-barang"
                value={formData.nama_barang}
                onChange={(e) =>
                  setFormData({ ...formData, nama_barang: e.target.value })
                }
                required
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => barangCancelButtonRef.current?.focus(),
                    onRight: () => kategoriSelectRef.current?.focus(),
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select
                value={formData.kategori_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, kategori_id: value })
                }
              >
                <SelectTrigger
                  ref={kategoriSelectRef}
                  data-testid="form-kategori"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => namaBarangInputRef.current?.focus(),
                      onRight: () => satuanInputRef.current?.focus(),
                    })
                  }
                >
                  <SelectValue placeholder="Pilih kategori" />
                </SelectTrigger>
                <SelectContent>
                  {kategori.map((kat) => (
                    <SelectItem key={kat.kategori_id} value={kat.kategori_id}>
                      {kat.nama_kategori}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Satuan</Label>
                <Input
                  ref={satuanInputRef}
                  data-testid="form-satuan"
                  value={formData.satuan}
                  onChange={(e) =>
                    setFormData({ ...formData, satuan: e.target.value })
                  }
                  placeholder="Kg, Pcs, dll"
                  required
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => kategoriSelectRef.current?.focus(),
                      onRight: () => konversiInputRef.current?.focus(),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Konversi Satuan (Opsional)</Label>
                <Input
                  ref={konversiInputRef}
                  value={formData.konversi_satuan_text}
                  onChange={(e) =>
                    setFormData({ ...formData, konversi_satuan_text: e.target.value })
                  }
                  placeholder="pak=40, bal=400 atau 1 pak=50 pcs"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => satuanInputRef.current?.focus(),
                      onRight: () => minStokInputRef.current?.focus(),
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Bisa isi format teknis atau natural. Contoh: 1 pak=50 pcs.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Stok</Label>
                <Input
                  ref={minStokInputRef}
                  data-testid="form-min-stok"
                  type="number"
                  value={formData.min_stok}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_stok: parseFloat(e.target.value),
                    })
                  }
                  required
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => konversiInputRef.current?.focus(),
                      onRight: () => stokCurrentInputRef.current?.focus(),
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>{editMode ? "Stok Saat Ini (Sistem)" : "Stok Awal"}</Label>
                <Input
                  ref={stokCurrentInputRef}
                  data-testid="form-stok-current"
                  type="number"
                  value={formData.stok_current}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stok_current: parseFloat(e.target.value),
                    })
                  }
                  disabled={editMode}
                  required={!editMode}
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => minStokInputRef.current?.focus(),
                      onRight: () =>
                        editMode
                          ? stokAdjustModeRef.current?.focus()
                          : barangSubmitButtonRef.current?.focus(),
                    })
                  }
                />
              </div>
            </div>

            {editMode && (
              <div className="rounded-md border border-sky-200 bg-sky-50/60 p-4 space-y-4">
                <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm font-manrope font-semibold text-sky-900">
                    Stok Awal Bulan Ini (otomatis)
                  </p>
                    <p className="font-manrope font-bold text-sky-900">
                      {loadingStokAwalBulan
                        ? "Memuat..."
                      : `${formatStockValue(stokAwalBulan)} ${formData.satuan || ""}`}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tipe Penyesuaian</Label>
                    <Select
                      value={formData.stok_adjust_mode}
                      onValueChange={(value) =>
                        setFormData({ ...formData, stok_adjust_mode: value })
                      }
                    >
                      <SelectTrigger
                        ref={stokAdjustModeRef}
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => stokCurrentInputRef.current?.focus(),
                            onRight: () => stokAdjustQtyInputRef.current?.focus(),
                          })
                        }
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tambah">Tambah</SelectItem>
                        <SelectItem value="Kurang">Kurang</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                    <div className="space-y-2">
                      <Label>Qty Penyesuaian</Label>
                      <Input
                        ref={stokAdjustQtyInputRef}
                        type="number"
                        min="0"
                        step="any"
                        value={formData.stok_adjust_qty}
                        onChange={(e) =>
                          setFormData({ ...formData, stok_adjust_qty: e.target.value })
                        }
                        placeholder="Kosongkan jika tidak ada"
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => stokAdjustModeRef.current?.focus(),
                            onRight: () => stokAdjustNoteRef.current?.focus(),
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label>Keterangan Penyesuaian</Label>
                      <Textarea
                        ref={stokAdjustNoteRef}
                        value={formData.stok_adjust_note}
                        onChange={(e) =>
                          setFormData({ ...formData, stok_adjust_note: e.target.value })
                        }
                        rows={3}
                        placeholder="Contoh: stok fisik gudang"
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => stokAdjustQtyInputRef.current?.focus(),
                            onRight: () => barangSubmitButtonRef.current?.focus(),
                          })
                        }
                      />
                    </div>
                </div>

                {adjustmentQty > 0 && (
                  <p className="text-sm text-sky-800">
                    Perkiraan stok setelah penyesuaian: <span className="font-manrope font-bold">{formatStockValue(stokSetelahPenyesuaian)} {formData.satuan || ""}</span>
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                ref={barangCancelButtonRef}
                type="button"
                variant="outline"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => focusBarangFormEnd(),
                    onRight: () => barangSubmitButtonRef.current?.focus(),
                  })
                }
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                ref={barangSubmitButtonRef}
                data-testid="submit-barang-button"
                type="submit"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => barangCancelButtonRef.current?.focus(),
                  })
                }
              >
                {editMode ? "Update" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Barang dari Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">Panduan import:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Download template Excel terlebih dahulu</li>
                <li>Isi data sesuai format pada template</li>
                <li>Format wajib: Nama Barang, Kategori, Satuan, Min Stok, Stok Awal (tanpa harga)</li>
                <li>Kategori harus sudah ada di database</li>
                <li>Upload file Excel (.xlsx atau .xls)</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label>Pilih File Excel</Label>
              <Input
                data-testid="import-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setUploadFile(null);
              }}
            >
              Batal
            </Button>
            <Button
              data-testid="submit-import-button"
              onClick={handleImportExcel}
              disabled={!uploadFile}
            >
              <Upload size={16} className="mr-2" />
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={kartuStokDialogOpen}
        onOpenChange={(open) => {
          setKartuStokDialogOpen(open);
          if (!open) {
            setSelectedBarangDetail(null);
            setKartuStok(null);
            setActiveQuickFilter("all");
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Kartu Stok - {selectedBarangDetail?.nama_barang || "Detail Barang"}
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 space-y-4">
            <InlineHint className="border-slate-200 bg-white/80">
              Gunakan rentang tanggal atau quick filter untuk meninjau pergerakan stok tanpa mengubah data transaksi yang sudah tercatat.
            </InlineHint>

            <form onSubmit={handleApplyKartuStokFilter} className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
              <div className="space-y-2">
                <Label>Tanggal Mulai</Label>
                <Input
                  type="date"
                  value={kartuStokFilter.tanggalMulai}
                  onChange={(e) => {
                    setActiveQuickFilter("custom");
                    setKartuStokFilter((prev) => ({
                      ...prev,
                      tanggalMulai: e.target.value,
                    }));
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tanggal Akhir</Label>
                <Input
                  type="date"
                  value={kartuStokFilter.tanggalAkhir}
                  onChange={(e) => {
                    setActiveQuickFilter("custom");
                    setKartuStokFilter((prev) => ({
                      ...prev,
                      tanggalAkhir: e.target.value,
                    }));
                  }}
                  required
                />
              </div>
              <Button type="submit" variant="outline" disabled={loadingKartuStok} className="w-full md:w-auto">
                {loadingKartuStok ? "Memuat..." : "Terapkan Filter"}
              </Button>
            </form>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={activeQuickFilter === "today" ? "default" : "outline"}
                size="sm"
                onClick={() => handleQuickKartuStokFilter("today")}
              >
                Hari Ini
              </Button>
              <Button
                type="button"
                variant={activeQuickFilter === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => handleQuickKartuStokFilter("month")}
              >
                Bulan Ini
              </Button>
              <Button
                type="button"
                variant={activeQuickFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => handleQuickKartuStokFilter("all")}
              >
                Reset All Time
              </Button>
            </div>
          </div>

          {loadingKartuStok ? (
            <SectionLoadingState label="Memuat kartu stok barang" />
          ) : kartuStok ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Stok Awal</p>
                  <p className="font-manrope font-bold">
                    {formatStockValue(kartuStok.saldo_awal)} {kartuStok.satuan_dasar}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Total Masuk</p>
                  <p className="font-manrope font-bold text-green-700">
                    {formatStockValue(kartuStok.total_masuk)} {kartuStok.satuan_dasar}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Total Keluar</p>
                  <p className="font-manrope font-bold text-orange-700">
                    {formatStockValue(kartuStok.total_keluar)} {kartuStok.satuan_dasar}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Penyesuaian</p>
                  <p className="font-manrope font-bold text-sky-700">
                    {formatStockValue(kartuStok.total_penyesuaian)} {kartuStok.satuan_dasar}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Stok Akhir</p>
                  <p className="font-manrope font-bold text-primary">
                    {formatStockValue(kartuStok.saldo_akhir)} {kartuStok.satuan_dasar}
                  </p>
                </div>
              </div>

              <div className="space-y-3 md:hidden">
                {kartuStok.mutasi.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-white/70 px-4 py-6 text-center text-sm text-muted-foreground">
                    Tidak ada mutasi pada periode ini
                  </p>
                ) : (
                  kartuStok.mutasi.map((m, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-200/80 bg-white/90 p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{new Date(m.tanggal).toLocaleString("id-ID")}</p>
                          <p className="text-xs text-muted-foreground">{m.petugas || "-"}</p>
                        </div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          {m.tipe}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Qty Input</p>
                          <p>{formatStockValue(m.qty_input)} {m.satuan_input}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Qty Dasar</p>
                          <p>{formatStockValue(m.qty_base)} {kartuStok.satuan_dasar}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Stok</p>
                          <p className="font-medium">{formatStockValue(m.saldo_berjalan)} {kartuStok.satuan_dasar}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Keterangan</p>
                          <p>{m.keterangan || "-"}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="hidden overflow-x-auto rounded-md border md:block">
                <table className="min-w-[860px] w-full">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tanggal</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jenis</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty Input</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty Dasar</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stok</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Petugas</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kartuStok.mutasi.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-center text-muted-foreground" colSpan={7}>
                          Tidak ada mutasi pada periode ini
                        </td>
                      </tr>
                    ) : (
                      kartuStok.mutasi.map((m, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/30">
                          <td className="px-3 py-2">{new Date(m.tanggal).toLocaleString("id-ID")}</td>
                          <td className="px-3 py-2">{m.tipe}</td>
                          <td className="px-3 py-2">{formatStockValue(m.qty_input)} {m.satuan_input}</td>
                          <td className="px-3 py-2">{formatStockValue(m.qty_base)} {kartuStok.satuan_dasar}</td>
                          <td className="px-3 py-2 font-medium">{formatStockValue(m.saldo_berjalan)} {kartuStok.satuan_dasar}</td>
                          <td className="px-3 py-2">{m.petugas}</td>
                          <td className="px-3 py-2">{m.keterangan || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Pilih barang untuk melihat kartu stok
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default MasterBarangPage;
